import { ref, onBeforeUnmount, type Ref } from 'vue';
import * as Cesium from 'cesium';

interface AssetClickHandlers {
    onAssetSelected?: (asset: any | null) => void;
    onPositionUpdate?: (coords: any | null) => void;
    onPanelOpen?: () => void;
    onClearSelection?: () => void;
}

export function useClickHandler(
    viewer: Ref<Cesium.Viewer | null>,
    handlers?: AssetClickHandlers
) {
    const selectedFeature = ref<Cesium.Cesium3DTileFeature | null>(null);
    let handler: Cesium.ScreenSpaceEventHandler | null = null;

    const handleClick = (click: { position: Cesium.Cartesian2 }) => {
        if (!viewer.value) return;

        const pickedObject = viewer.value.scene.pick(click.position);

        if (Cesium.defined(pickedObject) && pickedObject instanceof Cesium.Cesium3DTileFeature) {
            const pickedTileset = pickedObject.tileset;

            const properties = pickedObject.getPropertyIds().reduce((acc, propId) => {
                acc[propId] = pickedObject.getProperty(propId);
                return acc;
            }, {} as Record<string, any>);

            const cartesian = viewer.value.scene.pickPosition(click.position);
            let coords = null;

            if (Cesium.defined(cartesian)) {
                const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
                coords = {
                    lat: Cesium.Math.toDegrees(cartographic.latitude),
                    lng: Cesium.Math.toDegrees(cartographic.longitude),
                    height: cartographic.height,
                };
            }

            return {
                feature: pickedObject,
                tileset: pickedTileset,
                properties,
                coords,
            };
        }

        return null;
    };

    const clearSelection = () => {
        // Reset previous highlight
        if (selectedFeature.value) {
            selectedFeature.value.color = Cesium.Color.WHITE;
            selectedFeature.value = null;
        }
        handlers?.onClearSelection?.();
        viewer.value?.scene.requestRender();
    };

    const handleAssetClick = (clickedData: {
        coords: any;
        feature: Cesium.Cesium3DTileFeature;
        properties: any;
    }) => {
        if (!clickedData?.coords) return;

        // reset previous selection highlight
        if (selectedFeature.value) {
            selectedFeature.value.color = Cesium.Color.WHITE;
        }

        clickedData.feature.color = Cesium.Color.fromCssColorString('#6610f2').withAlpha(0.7);
        viewer.value?.scene.requestRender();

        // update selected feature
        selectedFeature.value = clickedData.feature;

        // notify about new asset
        handlers?.onAssetSelected?.(clickedData);

        handlers?.onPositionUpdate?.(clickedData.coords);
        handlers?.onPanelOpen?.();
    };

    const initializeClick = (customCallback: any = null) => {
        if (!viewer.value) {
            console.warn('Viewer not initialized');
            return;
        }

        handler = new Cesium.ScreenSpaceEventHandler(viewer.value.scene.canvas);

        const callback = customCallback || handleAssetClick;

        handler.setInputAction((click: { position: Cesium.Cartesian2 }) => {
            const clickedData = handleClick(click);
            if (clickedData) callback(clickedData);
        }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    };

    onBeforeUnmount(() => {
        if (handler) {
            handler.destroy();
            handler = null;
        }
        if (viewer.value) viewer.value.canvas.style.cursor = 'default';
    });

    return {
        selectedFeature,
        initializeClick,
        handleClick,
        handleAssetClick,
        clearSelection,
    };
}
